import { Agent } from "@biblioteksentralen/cordata";
import { Box, Heading } from "@biblioteksentralen/react";
import {
  fetchAgentWorksDataAboutAgent,
  fetchAgentWorksDataForContributorRole,
  WorksData,
} from "../../../app/[locale]/sites/[siteDomain]/samling/personer/[agentBibbiIdAndNorafId]/fetchAgentWorksData";
import { useCommonData } from "../../../components/layout/CommonDataProvider";
import { useMultipleUrlStates } from "../../../utils/hooks/useMultipleUrlStates/useMultipleUrlStates";
import { getSearchConfig } from "../../searchApi/searchConfig";
import { SeeMoreResultsButton } from "../search/SeeMoreResultsButton";
import { z } from "zod";
import useSWRInfinite from "swr/infinite";
import { useEffect, useState } from "react";
import { sift } from "radash";
import { LCWork } from "@libry-content/integrations";
import ItemGrid from "../works/ItemGrid";

// Schema is equivalent of `{ aboutAgent: number } & Record<string, number>`. the roleCodes are dynamic and typed with string (not enum), so the record reflects this.
const agentUrlStateSchema = z.object({ aboutAgent: z.number().optional() }).and(z.record(z.number()));

const useAgentUrlState = () => {
  const [state, setState] = useMultipleUrlStates(agentUrlStateSchema);
  const paginate = (agentWorksSectionUrlParam: string) =>
    state?.[agentWorksSectionUrlParam]
      ? setState({
          ...state,
          [agentWorksSectionUrlParam]: state?.[agentWorksSectionUrlParam] + 1,
        })
      : setState({ ...state, [agentWorksSectionUrlParam]: 2 });
  return { state, paginate };
};

type AgentWorksSectionProps = {
  agentWorksSectionUrlParam: string;
  heading: string;
  agent: Agent;
  initialAgentWorksData: WorksData;
};

export const AgentWorksSection = ({
  agentWorksSectionUrlParam,
  heading,
  agent,
  initialAgentWorksData,
}: AgentWorksSectionProps) => {
  const { paginate } = useAgentUrlState();
  const {
    worksParams,
    endOfResults,
    isValidating: isLoading,
  } = useFetchMoreWorksForSection(agent.id, initialAgentWorksData, agentWorksSectionUrlParam);

  const [autoFocusIndex, setAutoFocusIndex] = useState<number | undefined>(undefined);

  const paginateAndFocus = () => {
    //sets focus on first item of next pagination
    setAutoFocusIndex(worksParams.length);
    paginate(agentWorksSectionUrlParam);
  };

  const hasMore = typeof endOfResults !== "undefined" && !endOfResults;

  return (
    <Box as="section" width="100%">
      <Heading as="h2" fontSize="3xl" marginBottom=".5rem">
        {heading}
      </Heading>
      <ItemGrid items={worksParams.map((workParams) => new LCWork(workParams))} autoFocusIndex={autoFocusIndex} />
      {hasMore && <SeeMoreResultsButton marginTop="2rem" loading={isLoading} onClick={paginateAndFocus} />}
    </Box>
  );
};

type RoleCodeKey = { roleCode: string; page: number };

const useFetchMoreWorksForSection = (
  agentId: string,
  initialWorksData: WorksData,
  agentWorksSectionUrlParam: string
): WorksData => {
  const { site } = useCommonData();
  const searchConfig = getSearchConfig(site);
  const { state } = useAgentUrlState();
  const isAboutAgent = agentWorksSectionUrlParam === "aboutAgent";
  const pageState = state?.[agentWorksSectionUrlParam];

  const getKey = (pageIndex: number, previousPageData: WorksData): RoleCodeKey | null => {
    if (previousPageData && previousPageData.endOfResults) return null;
    return { roleCode: agentWorksSectionUrlParam, page: pageIndex };
  };

  const { data, isValidating, setSize } = useSWRInfinite(
    getKey,
    ({ roleCode, page }) => {
      if (page == 0) return undefined;

      return isAboutAgent
        ? fetchAgentWorksDataAboutAgent({ agentId, page: page + 1, searchConfig })
        : fetchAgentWorksDataForContributorRole({ agentId, roleCode, page: page + 1, searchConfig });
    },
    { revalidateIfStale: false, revalidateOnFocus: false }
  );

  useEffect(() => {
    setSize(pageState !== undefined ? pageState : 1);
  }, [setSize, pageState]);

  const filteredData = sift([initialWorksData, ...(data ?? [])]);
  const endOfResults = filteredData?.slice(-1)[0]?.endOfResults;

  return {
    worksParams: filteredData.flatMap(({ worksParams }) => worksParams),
    endOfResults: endOfResults !== undefined ? endOfResults : true,
    isValidating,
  };
};
